// =============================================================================
// HUB FINANCE - Page Pennylane (Facturation)
// =============================================================================
// Synchronisation avec Pennylane : Liste des factures, création, statuts
// =============================================================================

import { useState, useEffect } from "react";
import {
  Euro,
  Search,
  RefreshCw,
  Calendar,
  FileText,
  AlertCircle,
  CheckCircle2,
  Clock,
  Send,
  Filter,
  Download,
  ExternalLink,
  Building2,
  User,
  Plus,
  Upload,
  EyeOff,
  Eye
} from "lucide-react";
import { Badge } from "../../components/ui/badge";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { formatDateFR } from "../../utils/dateFormat";
import { toast } from "sonner";
import { formatClientFullName } from "../../utils/clientFormat";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../components/ui/select";

const API = process.env.REACT_APP_BACKEND_URL;

// Statuts de facture (style Pennylane)
const STATUT_FACTURE = {
  brouillon: { label: "Brouillon", color: "bg-zinc-800/30 text-slate-400", icon: FileText },
  a_venir: { label: "À venir", color: "bg-blue-500/20 text-blue-400", icon: Clock },
  en_retard: { label: "En retard", color: "bg-red-500/20 text-red-400", icon: AlertCircle },
  encaissee: { label: "Encaissée", color: "bg-green-500/20 text-green-400", icon: CheckCircle2 },
  part_encaissee: { label: "Part. encaissée", color: "bg-[var(--oh-stat-green-bg)] text-[var(--oh-stat-green-text)]", icon: Clock },
  annulee: { label: "Annulée", color: "bg-zinc-800/30 text-gray-400", icon: AlertCircle },
  avoir: { label: "Avoir", color: "bg-[var(--oh-stat-orange-bg)] text-[var(--oh-stat-orange-text)]", icon: AlertCircle },
};

// Mapping statuts Pennylane API (anglais) → statuts UI (français)
// Refonte P0 : domaine hub Pennylane (factures Pennylane brutes), distinct du
// prédicat dossier helpers.js:isFacturePayee → volontairement NON unifié.
const mapPennylaneStatus = (status, paymentStatus, paidAt, remainingAmount) => {
  if (status === "paid" || paymentStatus === "paid" || paidAt) return "encaissee";
  if (status === "partially_paid" || paymentStatus === "partially_paid") return "part_encaissee";
  if (status === "late" || status === "overdue") return "en_retard";
  if (status === "draft") return "brouillon";
  if (status === "finalized" || status === "upcoming") return "a_venir";
  if (status === "cancelled" || status === "canceled") return "annulee";
  if (remainingAmount !== undefined && remainingAmount !== null && parseFloat(remainingAmount) === 0) return "encaissee";
  return "brouillon";
};

export default function PennylanePage({ tenantId, onNavigate }) {
  const [factures, setFactures] = useState([]);
  const [facturesExclues, setFacturesExclues] = useState([]);
  const [dossiersAFacturer, setDossiersAFacturer] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("toutes");
  const [searchQuery, setSearchQuery] = useState("");
  const [stats, setStats] = useState({
    ca_facture: 0,
    ca_paye: 0,
    factures_en_retard: 0,
    montant_en_retard: 0,
    factures_non_envoyees: 0,
    montant_non_envoye: 0
  });
  const [pennylaneConnected, setPennylaneConnected] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [pipeline, setPipeline] = useState({});
  const [pipelineStats, setPipelineStats] = useState({});
  const [lastSync, setLastSync] = useState(null);
  const [pipelineTab, setPipelineTab] = useState("a_facturer");
  const [excluSyncCount, setExcluSyncCount] = useState(0);
  const [resolvingClients, setResolvingClients] = useState({});
  
  // Filtres de période
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [showPeriodFilter, setShowPeriodFilter] = useState(false);
  const [selectedPeriod, setSelectedPeriod] = useState("all"); // Période prédéfinie sélectionnée
  const [totalInvoices, setTotalInvoices] = useState(0);
  
  // Préréglages de période (style Pennylane)
  const PERIOD_PRESETS = [
    { id: "all", label: "Toutes les factures", dateFrom: "", dateTo: "" },
    { id: "current_month", label: "Ce mois-ci", getRange: () => {
      const now = new Date();
      const start = new Date(now.getFullYear(), now.getMonth(), 1);
      const end = new Date(now.getFullYear(), now.getMonth() + 1, 0);
      return { dateFrom: start.toISOString().split('T')[0], dateTo: end.toISOString().split('T')[0] };
    }},
    { id: "last_7_days", label: "7 derniers jours", getRange: () => {
      const now = new Date();
      const start = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
      return { dateFrom: start.toISOString().split('T')[0], dateTo: now.toISOString().split('T')[0] };
    }},
    { id: "last_30_days", label: "30 derniers jours", getRange: () => {
      const now = new Date();
      const start = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);
      return { dateFrom: start.toISOString().split('T')[0], dateTo: now.toISOString().split('T')[0] };
    }},
    { id: "last_month", label: "Mois dernier", getRange: () => {
      const now = new Date();
      const start = new Date(now.getFullYear(), now.getMonth() - 1, 1);
      const end = new Date(now.getFullYear(), now.getMonth(), 0);
      return { dateFrom: start.toISOString().split('T')[0], dateTo: end.toISOString().split('T')[0] };
    }},
    { id: "year_2026", label: "Année 2026", dateFrom: "2026-01-01", dateTo: "2026-12-31" },
    { id: "year_2025", label: "Année 2025", dateFrom: "2025-01-01", dateTo: "2025-12-31" },
    { id: "year_2024", label: "Année 2024", dateFrom: "2024-01-01", dateTo: "2024-12-31" },
    { id: "custom", label: "Période personnalisée", dateFrom: "", dateTo: "" }
  ];

  useEffect(() => {
    loadData();
  }, [tenantId]);

  // Fonction pour appliquer une période prédéfinie
  const applyPeriodPreset = (presetId) => {
    const preset = PERIOD_PRESETS.find(p => p.id === presetId);
    if (!preset) return;
    
    setSelectedPeriod(presetId);
    
    if (preset.getRange) {
      const range = preset.getRange();
      setDateFrom(range.dateFrom);
      setDateTo(range.dateTo);
    } else {
      setDateFrom(preset.dateFrom || "");
      setDateTo(preset.dateTo || "");
    }
    
    if (presetId !== "custom") {
      setShowPeriodFilter(false);
    }
  };

  // Calculer les stats dynamiquement selon les filtres de période
  const getDisplayStats = () => {
    let filtered = factures;
    
    // Appliquer le filtre de période
    if (dateFrom) {
      filtered = filtered.filter(f => (f.date_emission || "") >= dateFrom);
    }
    if (dateTo) {
      filtered = filtered.filter(f => (f.date_emission || "") <= dateTo);
    }
    
    const s = {
      ca_facture: 0, ca_paye: 0,
      factures_en_retard: 0, montant_en_retard: 0,
      factures_non_envoyees: 0, montant_non_envoye: 0
    };
    
    const today = new Date().toISOString().split("T")[0];
    
    for (const f of filtered) {
      if (f.statut === "annulee") continue;
      
      const ht = f.montant_ht || 0;
      s.ca_facture += ht;
      
      if (f.statut === "encaissee") {
        s.ca_paye += ht;
      } else {
        const remaining = f.restant_du || ht;
        
        if (f.statut === "en_retard" || (f.date_echeance && f.date_echeance < today && remaining > 0)) {
          s.factures_en_retard += 1;
          s.montant_en_retard += remaining;
        }
        
        if (!f.envoye && f.statut !== "brouillon" && remaining > 0) {
          s.factures_non_envoyees += 1;
          s.montant_non_envoye += ht;
        }
      }
    }
    
    return s;
  };

  const displayStats = loading ? stats : getDisplayStats();

  const getHeaders = () => {
    const token = localStorage.getItem('session_token');
    return {
      "X-Tenant-ID": tenantId || "climatpac",
      "Content-Type": "application/json",
      ...(token && { "Authorization": `Bearer ${token}` })
    };
  };

  const loadData = async () => {
    setLoading(true);
    try {
      // Charger depuis le CACHE LOCAL (instantané - pas d'appel API Pennylane)
      const cacheRes = await fetch(`${API}/api/pennylane/cached-overview`, {
        headers: getHeaders(), credentials: 'include'
      });
      
      if (cacheRes.ok) {
        const data = await cacheRes.json();
        if (data.success) {
          setPennylaneConnected(true);
          
          // Stats
          if (data.stats) setStats(data.stats);
          
          // Factures depuis le cache — mapper vers les clés attendues par le template
          const enriched = (data.factures || []).map(f => ({
            id: f.pennylane_id,
            pennylane_id: f.pennylane_id,
            invoice_number: f.numero || f.invoice_number,
            date_emission: f.date,
            date_echeance: f.deadline,
            statut: mapPennylaneStatus(f.statut, f.payment_status, f.paid_at, f.remaining_amount),
            raw_statut: f.statut,
            payment_status: f.payment_status,
            montant_ht: f.montant_ht || 0,
            restant_du: f.remaining_amount != null ? f.remaining_amount : (f.montant_ht || 0),
            client: { nom: f.client_nom || "Client inconnu" },
            envoye: !!f.sent_at,
            paid_at: f.paid_at,
            file_url: f.file_url,
            dossier_id: f.dossier_id,
            customer_id: f.customer_id,
          }));
          setFactures(enriched);
          setTotalInvoices(enriched.length);
          
          // Factures exclues (annulées + avoirs) — séparées
          const exclues = (data.factures_exclues || []).map(f => ({
            id: f.pennylane_id,
            pennylane_id: f.pennylane_id,
            invoice_number: f.numero || f.invoice_number,
            date_emission: f.date,
            date_echeance: f.deadline,
            statut: f.exclusion_raison === "avoir" ? "avoir" : "annulee",
            raw_statut: f.statut,
            payment_status: f.payment_status,
            montant_ht: f.montant_ht || 0,
            restant_du: 0,
            client: { nom: f.client_nom || "Client inconnu" },
            envoye: !!f.sent_at,
            paid_at: f.paid_at,
            file_url: f.file_url,
            dossier_id: f.dossier_id,
            exclusion_raison: f.exclusion_raison,
          }));
          setFacturesExclues(exclues);
          
          // Compteur de factures exclues manuellement (exclu_sync)
          setExcluSyncCount(data.exclu_sync_count || 0);
          
          // Pipeline dossiers
          if (data.pipeline) {
            setPipeline(data.pipeline);
            setPipelineStats(data.pipeline_stats || {});
          }
          
          // Dossiers à facturer
          setDossiersAFacturer(data.pipeline?.a_facturer || []);
          
          setLastSync(data.last_sync);
        }
      }
    } catch (e) {
      console.error("Erreur chargement données:", e);
    } finally {
      setLoading(false);
    }
  };

  const syncFromPennylane = async () => {
    setSyncing(true);
    toast.info("Synchronisation avec Pennylane en cours...");
    try {
      const res = await fetch(`${API}/api/pennylane/sync-cache`, {
        method: "POST", headers: getHeaders(), credentials: 'include'
      });
      if (res.ok) {
        const data = await res.json();
        // BUG-12 fix message delta (decision JJ 2026-05-04) — n'afficher
        // QUE les changements reels, pas les totaux scannes.
        const newInv = data.new_invoices || 0;
        const changedInv = data.changed_invoices || 0;
        const newLinks = data.auto_linked || 0;
        const dossUpd = data.dossiers_updated || 0;
        const totalDelta = newInv + changedInv + newLinks + dossUpd;

        if (totalDelta === 0) {
          toast.success(`Aucun changement (${data.synced} factures déjà à jour)`);
        } else {
          const parts = [];
          if (newInv > 0) parts.push(`${newInv} nouvelle${newInv > 1 ? "s" : ""} facture${newInv > 1 ? "s" : ""}`);
          if (changedInv > 0) parts.push(`${changedInv} facture${changedInv > 1 ? "s" : ""} mise${changedInv > 1 ? "s" : ""} à jour`);
          if (newLinks > 0) parts.push(`${newLinks} dossier${newLinks > 1 ? "s" : ""} nouvellement lié${newLinks > 1 ? "s" : ""}`);
          if (dossUpd > 0) parts.push(`${dossUpd} statut${dossUpd > 1 ? "s" : ""} mis à jour`);
          toast.success(parts.join(", "));
        }
        await loadData(); // Recharger depuis le cache mis à jour
      } else {
        toast.error("Erreur synchronisation Pennylane");
      }
    } catch (e) {
      toast.error("Erreur réseau");
    } finally {
      setSyncing(false);
    }
  };
  const handleCreateInvoice = async (dossierId) => {
    try {
      setSyncing(true);
      const res = await fetch(`${API}/api/pennylane/create-invoice/${dossierId}`, {
        method: 'POST',
        headers: getHeaders(),
        credentials: 'include',
        body: JSON.stringify({})
      });
      
      if (res.ok) {
        await loadData();
      } else {
        const error = await res.json();
        console.error("Erreur création facture:", error);
        alert("Erreur: " + (error.detail || error.error || "Impossible de créer la facture"));
      }
    } catch (error) {
      console.error("Erreur création facture:", error);
    } finally {
      setSyncing(false);
    }
  };

  const handleSyncCustomers = async () => {
    try {
      setSyncing(true);
      const res = await fetch(`${API}/api/pennylane/sync/customers/to-pennylane`, {
        method: 'POST',
        headers: getHeaders(),
        credentials: 'include'
      });
      
      if (res.ok) {
        const result = await res.json();
        alert(`Synchronisation terminée: ${result.created} créés, ${result.synced} synchronisés`);
      }
    } catch (error) {
      console.error("Erreur sync clients:", error);
    } finally {
      setSyncing(false);
    }
  };

  // Fix 4 — Exclure une facture du sync
  const handleExcludeFacture = async (pennylaneId, e) => {
    e.stopPropagation();
    try {
      const res = await fetch(`${API}/api/pennylane/factures/${pennylaneId}/exclure`, {
        method: 'PATCH',
        headers: getHeaders(),
        credentials: 'include',
        body: JSON.stringify({ raison: "Non AMO / B2B" })
      });
      if (res.ok) {
        toast.success("Facture exclue du sync");
        await loadData();
      } else {
        const err = await res.json().catch(() => ({}));
        toast.error(err.detail || "Erreur exclusion");
      }
    } catch (e) {
      toast.error("Erreur réseau");
    }
  };

  // Fix 3 — Résolution nom client à la volée
  const resolveClientName = async (customerId, factureId) => {
    if (!customerId || resolvingClients[customerId]) return;
    setResolvingClients(prev => ({ ...prev, [customerId]: true }));
    try {
      const res = await fetch(`${API}/api/pennylane/customer/${customerId}`, {
        headers: getHeaders(), credentials: 'include'
      });
      if (res.ok) {
        const data = await res.json();
        if (data.success && data.name) {
          // Mettre à jour localement
          setFactures(prev => prev.map(f =>
            f.customer_id === customerId
              ? { ...f, client: { ...f.client, nom: data.name } }
              : f
          ));
        }
      }
    } catch (e) {
      console.error("Erreur résolution client:", e);
    } finally {
      setResolvingClients(prev => ({ ...prev, [customerId]: false }));
    }
  };


  const formatMontant = (montant) => {
    return new Intl.NumberFormat("fr-FR", {
      style: "currency",
      currency: "EUR",
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(montant || 0);
  };

  const formatDate = formatDateFR;

  const getClientName = (client) => {
    if (!client) return "Client inconnu";
    // Structure simple depuis le cache: { nom: "NOM" }
    if (client.nom) return client.nom;
    // Structure Pennylane originale
    if (client.type_client === "professionnel") {
      return client.raison_sociale || client.nom || "Entreprise";
    }
    if (client.name) return client.name;
    if (client.nom && client.prenom && client.nom.includes(client.prenom)) {
      return client.nom;
    }
    return formatClientFullName(client) || "Client";
  };

  // Filtrer les factures selon l'onglet actif et la période
  const getFilteredFactures = () => {
    // Onglets "annulee" et "avoir" utilisent les factures exclues
    if (activeTab === "annulee" || activeTab === "avoir") {
      let filtered = facturesExclues.filter(f => f.statut === activeTab);
      if (searchQuery) {
        const query = searchQuery.toLowerCase();
        filtered = filtered.filter(f => 
          (f.invoice_number && f.invoice_number.toLowerCase().includes(query)) ||
          getClientName(f.client).toLowerCase().includes(query)
        );
      }
      return filtered;
    }
    
    let filtered = factures;
    
    // Filtre par période (appliqué localement)
    if (dateFrom) {
      filtered = filtered.filter(f => (f.date_emission || "") >= dateFrom);
    }
    if (dateTo) {
      filtered = filtered.filter(f => (f.date_emission || "") <= dateTo);
    }
    
    // Filtre par onglet (utilise le statut mappé en français)
    if (activeTab !== "toutes") {
      filtered = filtered.filter(f => f.statut === activeTab);
    }
    
    // Filtre par recherche
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(f => 
        (f.invoice_number && f.invoice_number.toLowerCase().includes(query)) ||
        getClientName(f.client).toLowerCase().includes(query)
      );
    }
    
    return filtered;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64" data-testid="pennylane-loading">
        <RefreshCw className="w-8 h-8 text-purple-400 animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="pennylane-page">
      {/* En-tête */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-white flex items-center gap-3">
            <Euro className="w-7 h-7 text-purple-400" />
            Factures Clients
          </h1>
          <div className="text-slate-400 mt-1 flex items-center gap-2">
            Synchronisation avec Pennylane
            {pennylaneConnected && (
              <Badge className="bg-green-500/20 text-green-400">Connecté</Badge>
            )}
          </div>
        </div>
        <div className="flex gap-2">
          <Button 
            variant="outline" 
            className="border-slate-600"
            onClick={handleSyncCustomers}
            disabled={syncing}
          >
            {syncing ? <RefreshCw className="w-4 h-4 mr-2 animate-spin" /> : <RefreshCw className="w-4 h-4 mr-2" />}
            Sync Clients
          </Button>
          <Button 
            className="bg-purple-600 hover:bg-purple-700"
            onClick={syncFromPennylane}
            disabled={syncing}
          >
            {syncing ? <RefreshCw className="w-4 h-4 mr-2 animate-spin" /> : <RefreshCw className="w-4 h-4 mr-2" />}
            {syncing ? "Sync Pennylane..." : "Sync Pennylane"}
          </Button>
          {lastSync && (
            <span className="text-xs text-slate-500 self-center">
              Dernière sync: {formatDate(lastSync)}
            </span>
          )}
        </div>
      </div>

      {/* Statistiques - Style Pennylane */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4" data-testid="pennylane-stats">
        <div className="bg-slate-800/50 border border-slate-700/50 rounded-xl p-4">
          <div className="flex items-center justify-between mb-2">
            <span className="text-slate-400 text-sm">CA facturé</span>
            <AlertCircle className="w-4 h-4 text-zinc-400" />
          </div>
          <p className="text-2xl font-bold text-white">{formatMontant(displayStats.ca_facture)}</p>
          <p className="text-zinc-400 text-xs mt-1">HT</p>
        </div>

        <div className="bg-slate-800/50 border border-slate-700/50 rounded-xl p-4">
          <div className="flex items-center justify-between mb-2">
            <span className="text-slate-400 text-sm">CA payé</span>
            <CheckCircle2 className="w-4 h-4 text-zinc-400" />
          </div>
          <p className="text-2xl font-bold text-green-400">{formatMontant(displayStats.ca_paye)}</p>
          <p className="text-zinc-400 text-xs mt-1">HT</p>
        </div>

        <div className="bg-red-500/10 border border-red-500/30 rounded-xl p-4">
          <div className="flex items-center justify-between mb-2">
            <span className="text-red-400 text-sm flex items-center gap-1">
              Factures en retard
              <Badge className="bg-red-500 text-white text-xs px-1.5">{displayStats.factures_en_retard}</Badge>
            </span>
          </div>
          <p className="text-2xl font-bold text-red-400">{formatMontant(displayStats.montant_en_retard)}</p>
          <p className="text-red-400/60 text-xs mt-1">HT</p>
        </div>

        <div className="bg-amber-500/10 border border-amber-500/30 rounded-xl p-4">
          <div className="flex items-center justify-between mb-2">
            <span className="text-amber-400 text-sm flex items-center gap-1">
              Factures non envoyées
              <Badge className="bg-amber-500 text-white text-xs px-1.5">{displayStats.factures_non_envoyees}</Badge>
            </span>
          </div>
          <p className="text-2xl font-bold text-amber-400">{formatMontant(displayStats.montant_non_envoye)}</p>
          <p className="text-amber-400/60 text-xs mt-1">HT</p>
        </div>
      </div>

      {/* Recherche et filtres */}
      <div className="flex flex-wrap items-center gap-4">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-400" />
          <Input
            placeholder="Rechercher une facture..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-10 bg-slate-800/50 border-slate-700"
            data-testid="pennylane-search"
          />
        </div>
        
        <div className="flex items-center gap-2 relative">
          <Button 
            variant="outline" 
            size="sm" 
            className={`border-slate-600 ${showPeriodFilter ? "bg-purple-500/20 border-purple-500" : ""}`}
            onClick={() => setShowPeriodFilter(!showPeriodFilter)}
            data-testid="period-filter-btn"
          >
            <Calendar className="w-4 h-4 mr-1" />
            {selectedPeriod !== "all" ? PERIOD_PRESETS.find(p => p.id === selectedPeriod)?.label : "Période"}
            {selectedPeriod !== "all" && <Badge className="ml-1 bg-purple-500 text-xs">Actif</Badge>}
          </Button>
          
          {/* Popup de filtre de période - Style Pennylane */}
          {showPeriodFilter && (
            <div className="absolute top-10 right-0 z-50 bg-slate-800 border border-slate-700 rounded-lg shadow-xl w-80" data-testid="period-filter-popup">
              <div className="p-3 border-b border-slate-700">
                <h4 className="text-white font-medium">Filtrer par période</h4>
              </div>
              
              {/* Préréglages rapides */}
              <div className="p-2 space-y-1">
                {PERIOD_PRESETS.filter(p => p.id !== "custom").map((preset) => (
                  <button
                    key={preset.id}
                    onClick={() => applyPeriodPreset(preset.id)}
                    className={`w-full text-left px-3 py-2 rounded text-sm transition-colors ${
                      selectedPeriod === preset.id 
                        ? "bg-[var(--oh-stat-purple-bg)] text-[var(--oh-stat-purple-text)] border border-[var(--oh-stat-purple-border)]"
                        : "text-slate-300 hover:bg-slate-700/50"
                    }`}
                    data-testid={`period-preset-${preset.id}`}
                  >
                    {preset.label}
                  </button>
                ))}
              </div>
              
              {/* Séparateur */}
              <div className="border-t border-slate-700 mx-3"></div>
              
              {/* Période personnalisée */}
              <div className="p-3 space-y-3">
                <p className="text-slate-400 text-xs font-medium">Période personnalisée</p>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                  <div>
                    <label className="text-slate-400 text-xs">Du</label>
                    <Input
                      type="date"
                      value={dateFrom}
                      onChange={(e) => {
                        setDateFrom(e.target.value);
                        setSelectedPeriod("custom");
                      }}
                      className="bg-slate-900 border-slate-600 mt-1 text-sm"
                      data-testid="date-from-input"
                    />
                  </div>
                  <div>
                    <label className="text-slate-400 text-xs">Au</label>
                    <Input
                      type="date"
                      value={dateTo}
                      onChange={(e) => {
                        setDateTo(e.target.value);
                        setSelectedPeriod("custom");
                      }}
                      className="bg-slate-900 border-slate-600 mt-1 text-sm"
                      data-testid="date-to-input"
                    />
                  </div>
                </div>
                <div className="flex gap-2 pt-2">
                  <Button 
                    size="sm" 
                    variant="outline"
                    className="flex-1 border-slate-600"
                    onClick={() => {
                      applyPeriodPreset("all");
                      setShowPeriodFilter(false);
                    }}
                    data-testid="period-clear-btn"
                  >
                    Effacer
                  </Button>
                  <Button 
                    size="sm" 
                    className="flex-1 bg-purple-600 hover:bg-purple-700"
                    onClick={() => setShowPeriodFilter(false)}
                    data-testid="period-apply-btn"
                  >
                    Appliquer
                  </Button>
                </div>
              </div>
            </div>
          )}
        </div>
        
        {/* Indicateur du nombre total */}
        <div className="text-slate-400 text-sm" data-testid="invoice-count">
          {totalInvoices > 0 && (
            <span>
              {factures.length} factures actives
              {facturesExclues.length > 0 && <span className="text-zinc-500 ml-1">({facturesExclues.length} annulées/avoirs)</span>}
              {excluSyncCount > 0 && <span className="text-amber-500 ml-1">({excluSyncCount} exclues)</span>}
            </span>
          )}
        </div>
      </div>

      {/* Onglets de statut */}
      <div className="flex items-center gap-1 border-b border-slate-700/50 pb-2 flex-wrap" data-testid="pennylane-tabs">
        {[
          { key: "toutes", label: "Toutes", activeClass: "bg-[var(--oh-stat-purple-bg)] text-[var(--oh-stat-purple-text)] border-b-2 border-[var(--oh-stat-purple-border)]", badgeActive: "bg-purple-500" },
          { key: "brouillon", label: "Brouillon", activeClass: "bg-slate-500/20 text-slate-300 border-b-2 border-slate-500", badgeActive: "bg-slate-500" },
          { key: "a_venir", label: "À venir", activeClass: "bg-blue-500/20 text-blue-400 border-b-2 border-blue-500", badgeActive: "bg-blue-500" },
          { key: "en_retard", label: "En retard", activeClass: "bg-red-500/20 text-red-400 border-b-2 border-red-500", badgeActive: "bg-red-500" },
          { key: "encaissee", label: "Encaissée", activeClass: "bg-green-500/20 text-green-400 border-b-2 border-green-500", badgeActive: "bg-green-500" },
          { key: "part_encaissee", label: "Part. encaissée", activeClass: "bg-[var(--oh-stat-green-bg)] text-[var(--oh-stat-green-text)] border-b-2 border-[var(--oh-stat-green-border)]", badgeActive: "bg-teal-500" },
          { key: "annulee", label: "Annulée", activeClass: "bg-zinc-500/20 text-zinc-400 border-b-2 border-zinc-500", badgeActive: "bg-zinc-500", isExcluded: true },
          { key: "avoir", label: "Avoir", activeClass: "bg-[var(--oh-stat-orange-bg)] text-[var(--oh-stat-orange-text)] border-b-2 border-[var(--oh-stat-orange-border)]", badgeActive: "bg-orange-500", isExcluded: true },
        ].map(tab => {
          const count = tab.key === "toutes" 
            ? factures.length 
            : tab.isExcluded
              ? facturesExclues.filter(f => f.statut === tab.key).length
              : factures.filter(f => f.statut === tab.key).length;
          if (count === 0 && tab.key !== "toutes") return null;
          const isActive = activeTab === tab.key;
          return (
            <button
              key={tab.key}
              onClick={() => setActiveTab(tab.key)}
              className={`px-3 py-2 text-sm rounded-t ${isActive ? tab.activeClass : "text-slate-400 hover:text-white"}`}
              data-testid={`tab-${tab.key}`}
            >
              {tab.label} <Badge className={`ml-1 ${isActive ? tab.badgeActive : "bg-slate-600"} text-xs`}>{count}</Badge>
            </button>
          );
        })}
      </div>

      {/* Liste des factures */}
      {/* Charte §9 — table large (10 colonnes) : scroll horizontal DANS le conteneur
          (jamais le body). overflow-x-auto + min-w garde les colonnes lisibles sur mobile. */}
      <div className="bg-slate-800/50 border border-slate-700/50 rounded-xl overflow-x-auto" data-testid="pennylane-invoices-table">
        <table className="w-full min-w-[960px]">
          <thead className="bg-slate-700/30">
            <tr className="text-left text-slate-400 text-sm">
              <th className="p-4 font-medium">Statut</th>
              <th className="p-4 font-medium">Envoi</th>
              <th className="p-4 font-medium">Émission</th>
              <th className="p-4 font-medium">Échéance</th>
              <th className="p-4 font-medium">Client</th>
              <th className="p-4 font-medium">Numéro</th>
              <th className="p-4 font-medium text-right">Restant dû TTC</th>
              <th className="p-4 font-medium text-right">Montant HT</th>
              <th className="p-4 font-medium">Paiement</th>
              <th className="p-4 font-medium">Actions</th>
            </tr>
          </thead>
          <tbody>
            {getFilteredFactures().length === 0 ? (
              <tr>
                <td colSpan="9" className="p-8 text-center">
                  <FileText className="w-12 h-12 mx-auto text-zinc-400 mb-3" />
                  <p className="text-slate-400">Aucune facture trouvée</p>
                </td>
              </tr>
            ) : (
              getFilteredFactures().map((facture) => {
                const statutInfo = STATUT_FACTURE[facture.statut] || STATUT_FACTURE.brouillon;
                const IconStatut = statutInfo.icon;
                const isExcluded = facture.statut === "annulee" || facture.statut === "avoir";
                return (
                  <tr 
                    key={facture.id} 
                    className={`border-t border-slate-700/50 transition-colors ${isExcluded ? "opacity-60" : "hover:bg-slate-700/30 cursor-pointer"}`}
                    data-testid={`invoice-row-${facture.id}`}
                  >
                    <td className="p-4">
                      <Badge className={statutInfo.color}>
                        {statutInfo.label}
                      </Badge>
                    </td>
                    <td className="p-4">
                      {facture.envoye ? (
                        <CheckCircle2 className="w-5 h-5 text-green-500" />
                      ) : (
                        <Send className="w-5 h-5 text-zinc-400" />
                      )}
                    </td>
                    <td className="p-4 text-white">{formatDate(facture.date_emission)}</td>
                    <td className="p-4 text-white">{formatDate(facture.date_echeance)}</td>
                    <td className="p-4">
                      <div className="flex items-center gap-2">
                        {facture.client?.type_client === "professionnel" ? (
                          <Building2 className="w-4 h-4 text-blue-400" />
                        ) : (
                          <User className="w-4 h-4 text-purple-400" />
                        )}
                        <span className="text-white">{getClientName(facture.client)}</span>
                        {/* Fix 3 — Résolution automatique des noms Client #XXX */}
                        {getClientName(facture.client).startsWith("Client #") && facture.customer_id && (
                          <button
                            onClick={(e) => { e.stopPropagation(); resolveClientName(facture.customer_id, facture.id); }}
                            className="text-xs text-purple-400 hover:text-purple-300 underline"
                            disabled={resolvingClients[facture.customer_id]}
                            data-testid={`resolve-client-${facture.id}`}
                          >
                            {resolvingClients[facture.customer_id] ? "..." : "Résoudre"}
                          </button>
                        )}
                      </div>
                    </td>
                    <td className="p-4 text-slate-300 font-mono">{facture.invoice_number || "-"}</td>
                    <td className="p-4 text-right text-white font-medium">{formatMontant(facture.restant_du)}</td>
                    <td className="p-4 text-right text-white">{formatMontant(facture.montant_ht)}</td>
                    <td className="p-4">
                      {facture.statut === "encaissee" && (
                        <CheckCircle2 className="w-5 h-5 text-green-500" />
                      )}
                    </td>
                    <td className="p-4">
                      <div className="flex items-center gap-1">
                        {facture.dossier_id && !facture.zoho_synced && facture.has_zoho_folder && (
                          <Button
                            size="sm"
                            variant="outline"
                            className="border-blue-500 text-blue-400 hover:bg-blue-500/20"
                            onClick={async (e) => {
                              e.stopPropagation();
                              try {
                                const res = await fetch(`${API}/api/pennylane/sync-to-zoho/${facture.dossier_id}`, {
                                  method: 'POST',
                                  headers: getHeaders(),
                                  credentials: 'include'
                                });
                                if (res.ok) {
                                  toast.success("Facture synchronisée vers Zoho");
                                  loadData();
                                } else {
                                  const err = await res.json().catch(() => ({}));
                                  toast.error(err.detail || "Erreur synchronisation Zoho");
                                }
                              } catch (e) {
                                toast.error("Erreur: " + e.message);
                              }
                            }}
                            data-testid={`sync-zoho-${facture.id}`}
                          >
                            <Upload className="w-4 h-4 mr-1" /> Zoho
                          </Button>
                        )}
                        {facture.zoho_synced && (
                          <Badge className="bg-green-500/20 text-green-400">
                            <CheckCircle2 className="w-3 h-3 mr-1" /> Zoho
                          </Badge>
                        )}
                        {/* Fix 4 — Bouton exclure du sync */}
                        {!isExcluded && (
                          <Button
                            size="sm"
                            variant="ghost"
                            className="text-zinc-400 hover:text-red-400 hover:bg-red-500/10 px-2"
                            onClick={(e) => handleExcludeFacture(facture.pennylane_id, e)}
                            title="Exclure du sync (Non AMO / B2B)"
                            data-testid="btn-exclure-facture"
                          >
                            <EyeOff className="w-4 h-4" />
                          </Button>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>

    </div>
  );
}
